import React, { useState, useEffect, useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Autocomplete, TextField, Box, Typography, useTheme, alpha, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Server } from 'lucide-react';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import styles from './IndentDemandReport.module.css';
import Header from '../../Header';
import { useNavigate } from 'react-router-dom';
import { local_signage, local_url } from '../../Const';

export const Stock_EOD = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State for filters
  const [selectedArea, setSelectedArea] = useState('');
  const [selectedBranch, setSelectedBranch] = useState('');
  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [selectedItem, setSelectedItem] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [lifeRangeFrom, setLifeRangeFrom] = useState(1);
  const [lifeRangeTo, setLifeRangeTo] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  
  // NEW: Add State for Segments, Areas, Branches, and BranchType
  const [segments, setSegments] = useState([]);
  const [branchType, setBranchType] = useState('');
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedAreaObj, setSelectedAreaObj] = useState(null);
  const [selectedBranchObj, setSelectedBranchObj] = useState(null);
  const [selectedItemObj, setSelectedItemObj] = useState(null);
  
  // State for table
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Loading states for dropdowns
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  // Session timeout dialog state
  const [showSessionDialog, setShowSessionDialog] = useState(false);

  // Date validation state
  const [dateRangeError, setDateRangeError] = useState('');

  // Get user data from localStorage
  const userId = localStorage.getItem("userId");
  const authToken = localStorage.getItem("AuthorizationToken");

  // Date validation functions
  const validateDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) {
      return 'Both dates are required';
    }
    
    if (toDate.isBefore(fromDate)) {
      return 'To Date must be after From Date';
    }
    
    const daysDiff = toDate.diff(fromDate, 'day');
    if (daysDiff > 90) {
      return 'Date range cannot exceed 90 days';
    }
    
    return '';
  };

  const handleFromDateChange = (newValue) => {
    setFromDate(newValue);
    const error = validateDateRange(newValue, toDate);
    setDateRangeError(error);
  };

  const handleToDateChange = (newValue) => {
    setToDate(newValue);
    const error = validateDateRange(fromDate, newValue);
    setDateRangeError(error);
  };

  // Check if to date exceeds 90 days from from date
  const isToDateInvalid = () => {
    if (!fromDate || !toDate) return false;
    return toDate.diff(fromDate, 'day') > 90;
  };

  // Function to clear all localStorage and navigate to login
  const handleSessionTimeout = () => {
    localStorage.clear();
    setShowSessionDialog(false);
    alert('Session Timeout! Please login again.');
    navigate('/Login', { replace: true });
  };

  // Generic API call wrapper with 401 handling
  const apiCall = async (url, options = {}) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 60000);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (response.status === 401 || response.status === 403) {
        setShowSessionDialog(true);
        throw new Error('Session expired');
      }

      return response;
    } catch (error) {
      clearTimeout(timeout);
      if (error.name === 'AbortError') {
        alert('Request timed out. Please try again.');
      }
      if (error.message === 'Session expired') {
        throw error;
      }
      console.error('API call error:', error);
      throw error;
    }
  };

  // Session Timeout Dialog Component
  const SessionTimeoutDialog = () => (
    <Dialog
      open={showSessionDialog}
      onClose={() => {}}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle 
        sx={{ 
          backgroundColor: '#f44336', 
          color: 'white',
          textAlign: 'center',
          fontWeight: 'bold'
        }}
      >
        Session Timeout
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center', py: 3 }}>
        <Typography variant="h6" gutterBottom>
          Your session has expired
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Please login again to continue using the application.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
        <Button
          onClick={handleSessionTimeout}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          Login Again
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Date picker styling
  const datePickerStyle = {
    '& .MuiOutlinedInput-root': {
      height: '35px',
      fontSize: '14px',
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#dee2e6',
      },
      '&:hover fieldset': {
        borderColor: '#adb5bd',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#667eea',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '14px',
      color: '#6c757d',
    },
  };

  // Loader Component
  const FullPageLoader = () => (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(248, 250, 252, 0.85)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
      }}
    >
      <Box
        sx={{
          width: 100,
          height: 100,
          position: 'relative',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.primary.main,
            animation: 'spin 1s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '80%',
            height: '80%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.secondary?.main || '#1976d2',
            animation: 'spin 1.5s linear infinite',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            width: '60%',
            height: '60%',
            borderRadius: '50%',
            border: '3px solid transparent',
            borderTopColor: theme.palette.info?.main || '#0288d1',
            animation: 'spin 2s linear infinite',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'white',
            borderRadius: '50%',
            width: '40%',
            height: '40%',
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Server
            size={20}
            color={theme.palette.primary.main}
          />
        </Box>
      </Box>

      <Typography
        variant="h5"
        color="primary"
        mb={1}
        sx={{ fontWeight: 600 }}
      >
        Loading Stock Summary Data
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        align="center"
        sx={{
          maxWidth: '300px',
          animation: 'pulse 1.5s infinite ease-in-out',
        }}
      >
        Please wait while we fetch stock summary data...
      </Typography>

      <Box
        sx={{
          mt: 4,
          height: 4,
          width: 220,
          borderRadius: 2,
          overflow: 'hidden',
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            backgroundImage: `linear-gradient(90deg, 
              rgba(255,255,255,0), 
              ${theme.palette.primary.main}, 
              rgba(255,255,255,0))`,
            backgroundSize: '1000px 100%',
            animation: 'shimmer 2s infinite',
          }}
        />
      </Box>
    </Box>
  );

  // NEW: Fetch Segments on Mount
  useEffect(() => {
    const fetchSegments = async () => {
      try {
        const response = await apiCall(`${local_url}/api/BizSegmentbyParam/GetBizSegmentByUserId/${userId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSegments(data.filter(segment => segment.deleted === "N"));
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching business segments:', error);
        }
      }
    };

    if (authToken && userId) fetchSegments();
  }, [authToken, userId]);

  // NEW: Fetch Areas When BranchType Changes
  useEffect(() => {
    const fetchAreasBySegment = async () => {
      if (!branchType) return;

      setLoadingAreas(true);
      try {
        const response = await apiCall(`${local_signage}/api/v1/sfareabysegment?SegmentCode=${branchType}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setAreas(data);
          setSelectedAreaObj(null);
          setBranches([]);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching areas by segment:', error);
        }
        setAreas([]);
        setBranches([]);
        setSelectedAreaObj(null);
        setSelectedBranchObj(null);
      } finally {
        setLoadingAreas(false);
      }
    };

    if (authToken && branchType) fetchAreasBySegment();
  }, [branchType, authToken]);

  // NEW: Fetch Branches When Area or BranchType Changes
  useEffect(() => {
    const fetchBranchList = async () => {
      if (!branchType || !selectedAreaObj) return;

      setLoadingBranches(true);
      try {
        const areaParam = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || "ALL";
        const url = `${local_url}/api/Company/GetBranchList/${areaParam}/SEGMENT/${branchType}`;
        const response = await apiCall(url, {
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          // Add 'All' option to the beginning of branches list
          const branchesWithAll = [
            { branchID: 'ALL', branchName: 'All Branches', isAllOption: true },
            ...data
          ];
          setBranches(branchesWithAll);
          setSelectedBranchObj(null);
        }
      } catch (error) {
        if (error.message !== 'Session expired') {
          console.error('Error fetching branch list:', error);
        }
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (authToken && branchType && selectedAreaObj) fetchBranchList();
  }, [branchType, selectedAreaObj, authToken]);

  // Fetch Items (placeholder - you can implement this if needed)
  const fetchItems = async () => {
    try {
      // This endpoint might need to be adjusted based on your API
      setItems([{ ItemId: 'ALL', ItemName: 'ALL' }]);
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([{ ItemId: 'ALL', ItemName: 'ALL' }]);
    }
  };

  // Fetch Stock Summary Data
  const fetchStockSummaryData = async () => {
    if (!selectedAreaObj) {
      alert('Please select an Area to fetch data.');
      return;
    }

    if (!selectedBranchObj) {
      alert('Please select a Branch to fetch data.');
      return;
    }

    if (!fromDate) {
      alert('Please select a From Date.');
      return;
    }

    const dateError = validateDateRange(fromDate, toDate);
    if (dateError) {
      alert(dateError);
      return;
    }

    setLoading(true);
    setSortConfig({ key: null, direction: 'asc' });
    setCurrentPage(1);

    try {
      const areaId = selectedAreaObj.SFAreaId || selectedAreaObj.sfAreaId || 'ALL';
      const fromDateStr = fromDate.format('YYYYMMDD');
      
      // Conditional logic for BranchID parameter and option
      let branchIdParam;
      let optionParam;
      
      if (selectedBranchObj.isAllOption) {
        // If 'All Branches' is selected, pass the AreaID and use option=2
        branchIdParam = areaId;
        optionParam = 2;
      } else {
        // If a specific branch is selected, pass the BranchID and use option=1
        branchIdParam = selectedBranchObj.branchID;
        optionParam = 1;
      }

      console.log('Fetching data with params:', {
        areaId,
        branchIdParam,
        fromDateStr,
        optionParam,
        isAllBranches: selectedBranchObj.isAllOption
      });

      const url = `${local_signage}/api/v1/stocksummaryreport?BranchId=${branchIdParam}&BusinessDate=${fromDateStr}&option=${optionParam}`;
      
      const response = await apiCall(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Received data:', data);
        
        // Transform API data to match component structure
        const transformedData = data.map((item, index) => {
          return {
            id: index + 1,
            sno: index + 1,
            branchId: item.BranchID || 'N/A',
            branchName: item.Branchname || item.BranchName || selectedBranchObj?.branchName || 'N/A',
            itemId: item.ItemId || 'N/A',
            itemName: item.itemname || 'N/A',
            uom: item.UOM || 'N/A',
            openingQty: item.OpQty || 0,
            saleQty: item.SaleQty || 0,
            indentInQty: item.GRQty || 0,
            transferOutQty: item.TrOutQty || 0,
            consumeQty: item.CounsumeQty || 0,
            prodQty: item.ProdQty || 0,
            wastageQty: item.WastageQty || 0,
            closingQty: Array.isArray(item.ClQty) ? item.ClQty[0] || 0 : item.ClQty || 0
          };
        });

        setRawData(transformedData);
        setFilteredData(transformedData);

        if (transformedData.length === 0) {
          alert('No data found for the selected criteria.');
        }
      } else {
        const errorText = await response.text();
        console.error('Failed to fetch stock summary data:', response.status, errorText);
        alert(`Failed to fetch data: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      if (error.message !== 'Session expired') {
        console.error('Error fetching stock summary data:', error);
        alert('Error fetching data. Please check your connection and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Initialize data
  useEffect(() => {
    setRawData([]);
    setFilteredData([]);
    if (authToken && userId) {
      fetchItems();
    }
  }, [authToken, userId]);

  // Apply filters (now calls API)
  const applyFilters = () => {
    fetchStockSummaryData();
  };

  // Clear filters
  const clearFilters = () => {
    setSelectedAreaObj(null);
    setSelectedBranchObj(null);
    setSelectedItemObj(null);
    setSelectedBatch('');
    setBranchType('');
    setFromDate(dayjs().subtract(1, 'month'));
    setToDate(dayjs());
    setLifeRangeFrom(1);
    setLifeRangeTo(10);
    setSearchTerm('');
    setCurrentPage(1);
    setSortConfig({ key: null, direction: 'asc' });
    setRawData([]);
    setFilteredData([]);
    setDateRangeError('');
  };

  // Export function for stock summary data
  const exportToXLSX = () => {
    if (filteredAndSearchedData.length === 0) {
      alert('No data to export.');
      return;
    }

    const exportData = filteredAndSearchedData.map(row => ({
      'S.No': row.sno,
      'BRANCH ID': row.branchId,
      'Branch Name': row.branchName,
      'Item Id': row.itemId,
      'ItemName': row.itemName,
      'UOM': row.uom,
      'Opening Quantity': row.openingQty,
      'Sale Quantity': row.saleQty,
      'Indent In Qty': row.indentInQty,
      'Transfer Out Qty': row.transferOutQty,
      'Consumed Quantity': row.consumeQty,
      'Production QTY': row.prodQty,
      'Wastage Qty': row.wastageQty,
      'Closing Qty': row.closingQty
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Summary Report');
    XLSX.writeFile(wb, 'stock_summary_report.xlsx');
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Search and sort data with better search functionality
  const filteredAndSearchedData = useMemo(() => {
    let data = filteredData;

    if (searchTerm) {
      const lower = searchTerm.toLowerCase().trim();
      data = data.filter(item =>
        (item.itemName && item.itemName.toString().toLowerCase().includes(lower)) ||
        (item.itemId && item.itemId.toString().toLowerCase().includes(lower)) ||
        (item.branchId && item.branchId.toString().toLowerCase().includes(lower)) ||
        (item.branchName && item.branchName.toString().toLowerCase().includes(lower))
      );
    }

    if (!sortConfig.key) {
      return data.map((item, index) => ({
        ...item,
        sno: index + 1
      }));
    }

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Handle numeric sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle date sorting for specific date fields
      if (sortConfig.key === 'postingDate') {
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          const [day, month, year] = dateStr.split('-');
          return new Date(year, month - 1, day);
        };
        
        const aDate = parseDate(aValue);
        const bDate = parseDate(bValue);
        
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle string sorting
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      
      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    }).map((item, index) => ({
      ...item,
      sno: index + 1
    }));
  }, [filteredData, sortConfig, searchTerm]);

  // Function to get expiry status styling
  const getExpiryStatus = (daysToExpiry) => {
    if (daysToExpiry < 0) return { class: 'expired', text: 'Expired' };
    if (daysToExpiry <= 2) return { class: 'critical', text: 'Critical' };
    if (daysToExpiry <= 7) return { class: 'warning', text: 'Warning' };
    return { class: 'good', text: 'Good' };
  };

  // Auto-redirect to first page when search results change
  useEffect(() => {
    if (searchTerm || filteredAndSearchedData.length > 0) {
      const maxPossiblePages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
      if (currentPage > maxPossiblePages && maxPossiblePages > 0) {
        setCurrentPage(1);
      }
    }
  }, [searchTerm, filteredAndSearchedData.length, rowsPerPage, currentPage]);

  // Reset to first page when search term changes
  useEffect(() => {
    if (searchTerm) {
      setCurrentPage(1);
    }
  }, [searchTerm]);

  // Pagination
  const totalPages = Math.ceil(filteredAndSearchedData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredAndSearchedData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <>
      <Header />
      
      <div className={styles.indent_dashboard}>
        {/* Session Timeout Dialog */}
        <SessionTimeoutDialog />

        {/* Filters Section */}
        <div className={styles.indent_filters_section}>
          <div className={styles.indent_filters_grid}>
            {/* NEW: Segment Dropdown */}
            <div className={styles.indent_filter_group}>
              <select
                value={branchType}
                onChange={e => setBranchType(e.target.value)}
                className={styles.indent_filter_select}
                style={{
                  height: '35px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  padding: '0 8px',
                  minWidth: '200px',
                  color: '#495057',
                  outline: 'none',
                  transition: 'border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 0.2rem rgba(102, 126, 234, 0.25)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
                onMouseEnter={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#adb5bd';
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.target !== document.activeElement) {
                    e.target.style.borderColor = '#dee2e6';
                  }
                }}
              >
                <option value="" style={{ color: '#6c757d' }}>Select Segment</option>
                {segments.map(segment => (
                  <option key={segment.businessSegmentCode} value={segment.businessSegmentCode}>
                    {segment.businessSegmentName}
                  </option>
                ))}
              </select>
            </div>

            {/* UPDATED: Area Dropdown with All option */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={areas}
                getOptionLabel={(option) => option.SFAreaName || option.sfAreaName || ''}
                value={selectedAreaObj}
                onChange={(event, newValue) => {
                  setSelectedAreaObj(newValue);
                }}
                disabled={!branchType || loadingAreas}
                loading={loadingAreas}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingAreas ? "Loading Areas..." : "Select Area"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingAreas ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* UPDATED: Branch Dropdown with All option */}
            <div className={styles.indent_filter_group}>
              <Autocomplete
                options={branches}
                getOptionLabel={(option) => option.branchName || ''}
                value={selectedBranchObj}
                onChange={(event, newValue) => {
                  setSelectedBranchObj(newValue);
                }}
                disabled={!selectedAreaObj || loadingBranches}
                loading={loadingBranches}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={loadingBranches ? "Loading Branches..." : "Select Branch *"}
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                        '& fieldset': {
                          borderColor: !selectedBranchObj ? '#f44336' : '#dee2e6',
                        },
                        '&:hover fieldset': {
                          borderColor: !selectedBranchObj ? '#f44336' : '#adb5bd',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: !selectedBranchObj ? '#f44336' : '#667eea',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: !selectedBranchObj ? '#f44336' : '#6c757d',
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingBranches ? <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                border: '2px solid #e0e0e0',
                                borderTop: '2px solid #1976d2',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}
                            />
                          </Box> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} style={{
                    fontWeight: option.isAllOption ? 'bold' : 'normal',
                    backgroundColor: option.isAllOption ? '#e3f2fd' : 'transparent',
                    borderBottom: option.isAllOption ? '1px solid #ddd' : 'none'
                  }}>
                    {option.branchName}
                  </li>
                )}
                sx={{ minWidth: 200 }}
              />
            </div>

            {/* <div className={styles.indent_filter_group}>
              <Autocomplete
                options={items}
                getOptionLabel={(option) => option.ItemName || ''}
                value={selectedItemObj}
                onChange={(event, newValue) => {
                  setSelectedItemObj(newValue);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Item"
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '35px',
                        fontSize: '14px',
                        backgroundColor: '#ffffff',
                      }
                    }}
                  />
                )}
                sx={{ minWidth: 200 }}
              />
            </div> */}

            {/* <div className={styles.indent_filter_group}>
              <TextField
                label="Batch Number"
                value={selectedBatch}
                onChange={(e) => setSelectedBatch(e.target.value)}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '35px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                  }
                }}
              />
            </div> */}

            <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Business Date"
                  value={fromDate}
                  onChange={handleFromDateChange}
                  format="DD-MM-YYYY"
                  sx={{
                    ...datePickerStyle,
                    '& .MuiOutlinedInput-root': {
                      ...datePickerStyle['& .MuiOutlinedInput-root'],
                      '& fieldset': {
                        borderColor: dateRangeError && fromDate ? '#f44336' : '#dee2e6',
                      },
                      '&:hover fieldset': {
                        borderColor: dateRangeError && fromDate ? '#f44336' : '#adb5bd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: dateRangeError && fromDate ? '#f44336' : '#667eea',
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div>

            {/* <div className={styles.indent_filter_group}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={handleToDateChange}
                  format="DD-MM-YYYY"
                  sx={{
                    ...datePickerStyle,
                    '& .MuiOutlinedInput-root': {
                      ...datePickerStyle['& .MuiOutlinedInput-root'],
                      backgroundColor: isToDateInvalid() ? '#ffebee' : '#ffffff',
                      '& fieldset': {
                        borderColor: dateRangeError && toDate ? '#f44336' : '#dee2e6',
                      },
                      '&:hover fieldset': {
                        borderColor: dateRangeError && toDate ? '#f44336' : '#adb5bd',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: dateRangeError && toDate ? '#f44336' : '#667eea',
                      },
                    },
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      variant: "outlined"
                    }
                  }}
                />
              </LocalizationProvider>
            </div> */}

            {/* <div className={styles.indent_filter_group}>
              <TextField
                label="Life Range From"
                type="number"
                value={lifeRangeFrom}
                onChange={(e) => setLifeRangeFrom(Number(e.target.value))}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '35px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                  }
                }}
              />
            </div>

            <div className={styles.indent_filter_group}>
              <TextField
                label="Life Range To"
                type="number"
                value={lifeRangeTo}
                onChange={(e) => setLifeRangeTo(Number(e.target.value))}
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '35px',
                    fontSize: '14px',
                    backgroundColor: '#ffffff',
                  }
                }}
              />
            </div> */}
          </div>

          {/* Date Range Validation Message */}
          {dateRangeError && (
            <div style={{
              color: '#f44336',
              fontSize: '14px',
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: '#ffebee',
              border: '1px solid #ffcdd2',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
            }}>
              <span style={{ marginRight: '8px' }}>⚠️</span>
              {dateRangeError}
            </div>
          )}

          {/* Success Message for Valid Date Range */}
          {/* {!dateRangeError && fromDate && toDate && (
            <div style={{
              color: '#4caf50',
              fontSize: '14px',
              marginTop: '8px',
              padding: '8px 12px',
              backgroundColor: '#e8f5e8',
              border: '1px solid #c8e6c9',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
            }}>
              <span style={{ marginRight: '8px' }}>✅</span>
              Valid date range selected ({toDate.diff(fromDate, 'day')} days)
            </div>
          )} */}

          <div className={styles.indent_filter_actions}>
            <button 
              onClick={applyFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_primary}`} 
              disabled={loading || !!dateRangeError || !selectedBranchObj}
              style={{
                opacity: (loading || !!dateRangeError || !selectedBranchObj) ? 0.6 : 1,
                cursor: (loading || !!dateRangeError || !selectedBranchObj) ? 'not-allowed' : 'pointer'
              }}
            >
              {loading ? 'Loading...' : 'Apply'}
            </button>
            <button 
              onClick={clearFilters} 
              className={`${styles.indent_btn} ${styles.indent_btn_secondary}`}
            >
              Clear
            </button>
            <button 
              onClick={exportToXLSX} 
              className={`${styles.indent_btn} ${styles.indent_btn_success}`}
            >
              Export XLSX
            </button>
            <div className={styles.indent_search_group}>
              <input
                type="text"
                placeholder="Search by Item Name, Item ID, Branch ID, or Branch Name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.indent_search_input}
              />
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className={styles.indent_results_summary}>
          <span>Showing {paginatedData.length} of {filteredAndSearchedData.length} records</span>
          <div className={styles.indent_rows_per_page}>
            <label>Rows:</label>
            <select 
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className={styles.indent_rows_select}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={500}>500</option>
            </select>
          </div>
        </div>

        {/* Data Table Section */}
        <div className={styles.indent_table_section}>
          {loading ? (
            <FullPageLoader />
          ) : filteredAndSearchedData.length === 0 ? (
            <div className={styles.indent_no_data}>
              <p>No data found. Please select a Segment and Area, then click Apply to fetch data.</p>
            </div>
          ) : (
            <div className={styles.indent_table_container}>
              <table className={styles.indent_data_table}>
                <thead>
                  <tr>
                    <th className={styles.indent_non_sortable}>S.No</th>
                    <th onClick={() => handleSort('branchId')} className={styles.indent_sortable}>
                      BRANCH ID {sortConfig.key === 'branchId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('branchName')} className={styles.indent_sortable}>
                      Branch Name {sortConfig.key === 'branchName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('itemId')} className={styles.indent_sortable}>
                      Item Id {sortConfig.key === 'itemId' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('itemName')} className={styles.indent_sortable}>
                      ItemName {sortConfig.key === 'itemName' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('uom')} className={styles.indent_sortable}>
                      UOM {sortConfig.key === 'uom' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('openingQty')} className={styles.indent_sortable}>
                      Opening Quantity {sortConfig.key === 'openingQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('saleQty')} className={styles.indent_sortable}>
                      Sale Quantity {sortConfig.key === 'saleQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('indentInQty')} className={styles.indent_sortable}>
                      Indent In Qty {sortConfig.key === 'indentInQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('transferOutQty')} className={styles.indent_sortable}>
                      Transfer Out Qty {sortConfig.key === 'transferOutQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('consumeQty')} className={styles.indent_sortable}>
                      Consumed Quantity {sortConfig.key === 'consumeQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('prodQty')} className={styles.indent_sortable}>
                      Production QTY {sortConfig.key === 'prodQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('wastageQty')} className={styles.indent_sortable}>
                      Wastage Qty {sortConfig.key === 'wastageQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                    <th onClick={() => handleSort('closingQty')} className={styles.indent_sortable}>
                      Closing Qty {sortConfig.key === 'closingQty' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row) => {
                    return (
                      <tr key={row.id}>
                        <td>{row.sno}</td>
                        <td className={styles.indent_ticket_id}>{row.branchId}</td>
                        <td>{row.branchName}</td>
                        <td className={styles.indent_ticket_id}>{row.itemId}</td>
                        <td>{row.itemName}</td>
                        <td>{row.uom}</td>
                        <td className={styles.indent_amount}>{row.openingQty}</td>
                        <td className={styles.indent_amount}>{row.saleQty}</td>
                        <td className={styles.indent_amount}>{row.indentInQty}</td>
                        <td className={styles.indent_amount}>{row.transferOutQty}</td>
                        <td className={styles.indent_amount}>{row.consumeQty}</td>
                        <td className={styles.indent_amount}>{row.prodQty}</td>
                        <td className={styles.indent_amount}>{row.wastageQty}</td>
                        <td className={styles.indent_amount}>{row.closingQty}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredAndSearchedData.length > 0 && !loading && (
          <div className={styles.indent_pagination}>
            {/* First Page Button */}
            <button 
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              First
            </button>
            
            {/* Previous Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Prev
            </button>
            
            {/* Page Numbers */}
            <div className={styles.indent_page_numbers}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`${styles.indent_btn} ${styles.indent_btn_pagination} ${currentPage === page ? styles.indent_active : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
            </div>
            
            {/* Next Button */}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Next
            </button>
            
            {/* Last Page Button */}
            <button 
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className={`${styles.indent_btn} ${styles.indent_btn_pagination}`}
            >
              Last
            </button>
          </div>
        )}
      </div>

      {/* Add CSS animations */}
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }
        
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
};

export default Stock_EOD;